import { useEffect, useCallback, useRef, useState } from 'react';
import { useHQVDatalayerTrackingStore, HQVFallbackMode } from '../../modules/store/hqvDatalayerTrackingStore';
import { callDataLayer } from '../../organisms/HotelGalleryTitle/utils/helper';
import { hqvLog } from '../../constants';

interface UseHQVRetryOrchestrationProps {
  resolvedUrl?: string;
  sessionData?: unknown;
  headersData?: unknown;
  query?: unknown;
  propertyId?: string;
  brandCode?: string;
  datalayerParams?: unknown;
  datalayerProperties?: unknown;
  currentLocale?: string;
  onCloseModal?: () => void;
  modalOpenState?: boolean;
  // Mode flag - determines retry behavior
  enableUseSWR?: boolean;
  // SWR state props - passed from HQVWrapper
  propertyInfoError?: Error;
  photogalleryError?: Error;
  propertyInfoData?: unknown;
  photogalleryData?: unknown;
  refetchPropertyInfo?: () => Promise<unknown>;
  refetchPhotogallery?: () => Promise<unknown>;
  isPropertyInfoLoading?: boolean;
  isPhotogalleryLoading?: boolean;
}

interface UseHQVRetryOrchestrationReturn {
  hqvFallbackMode: HQVFallbackMode;
  isRetrying: boolean;
  retryAttempted: boolean;
}

/**
 * Custom hook to orchestrate HQV retry logic and fallback mode determination.
 *
 * RETRY BEHAVIOR:
 * - SWR Mode (enableUseSWR=true): SWR handles retries automatically (3 retries with exponential backoff)
 *   This hook only determines fallback mode and handles datalayer calls.
 * - Legacy Mode (enableUseSWR=false): This hook manually triggers one retry when both calls fail.
 *
 * Requirements:
 * 1. When both phoenixShopHQVPropertyInfoCall AND phoenixShopHQVPhotogalleryCall fail together:
 *    - SWR Mode: SWR retries automatically (up to 3 times)
 *    - Legacy Mode: Retry both calls (once)
 *
 * 2. If the retry is successful (at least one succeeds):
 *    - HQV loads with Property info and/or Photo Gallery
 *    - Update DL key "search_fallback_exists" to "true"
 *
 * 3. If the retry is unsuccessful for both Property info and Photo Gallery:
 *    - Load HQV with Amenities data only (hide Overview, Location, Information sections)
 *    - Update DL key "search_fallback_exists" to "true"
 *
 * 4. If retry unsuccessful for both AND amenities call also fails:
 *    - Close the HQV modal
 *    - Update DL key "search_fallback_exists" to "false"
 *
 * Fallback modes:
 * - 'normal': At least one of PropertyInfo/PhotoGallery succeeded (initial or retry)
 * - 'amenities_only': Both PropertyInfo and PhotoGallery failed (after retry) but amenities succeeded
 * - 'closed': All three calls failed - modal should be closed
 */
export const useHQVRetryOrchestration = ({
  resolvedUrl,
  sessionData,
  headersData,
  query,
  propertyId,
  brandCode,
  datalayerParams,
  datalayerProperties,
  currentLocale,
  onCloseModal,
  modalOpenState,
  enableUseSWR = false,
  // SWR state
  propertyInfoError,
  photogalleryError,
  propertyInfoData,
  photogalleryData,
  refetchPropertyInfo,
  refetchPhotogallery,
  isPropertyInfoLoading,
  isPhotogalleryLoading,
}: UseHQVRetryOrchestrationProps): UseHQVRetryOrchestrationReturn => {
  // Local state for retry tracking
  const [retryAttempted, setRetryAttempted] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryPropertyInfoSucceeded, setRetryPropertyInfoSucceeded] = useState<boolean | null>(null);
  const [retryPhotogallerySucceeded, setRetryPhotogallerySucceeded] = useState<boolean | null>(null);
  // Ref to prevent race conditions in retry logic
  const retryInProgressRef = useRef(false);

  // Store selectors - only for amenities and fallback mode
  const amenitiesCallStatus = useHQVDatalayerTrackingStore(state => state.amenitiesCallStatus);
  const hqvFallbackMode = useHQVDatalayerTrackingStore(state => state.hqvFallbackMode);

  // Store actions
  const setHqvFallbackMode = useHQVDatalayerTrackingStore(state => state.setHqvFallbackMode);
  const setDatalayerCallStatus = useHQVDatalayerTrackingStore(state => state.setDatalayerCallStatus);

  // Track if datalayer has been called for fallback scenarios
  const datalayerCalledForFallback = useRef(false);
  // Track if datalayer has been called for success scenario
  const datalayerCalledForSuccess = useRef(false);
  // Track if we've already processed the failure scenario (to prevent infinite loops)
  const failureProcessedRef = useRef(false);

  // Track previous modalOpenState to detect when modal reopens
  const prevModalOpenStateRef = useRef<boolean | undefined>(undefined);

  // Reset state when modal opens (new or reopening)
  useEffect(() => {
    // Reset when modal transitions from closed to open (includes reopening same property)
    if (modalOpenState && !prevModalOpenStateRef.current) {
      hqvLog.log('Modal opened - resetting retry state and datalayer refs');
      setRetryAttempted(false);
      setIsRetrying(false);
      setRetryPropertyInfoSucceeded(null);
      setRetryPhotogallerySucceeded(null);
      datalayerCalledForFallback.current = false;
      datalayerCalledForSuccess.current = false;
      retryInProgressRef.current = false;
      failureProcessedRef.current = false;
      setAmenitiesTimedOut(false);
      if (amenitiesTimeoutRef.current) {
        clearTimeout(amenitiesTimeoutRef.current);
        amenitiesTimeoutRef.current = null;
      }
    }
    prevModalOpenStateRef.current = modalOpenState;
  }, [modalOpenState]);

  // Helper to check if amenities call succeeded
  const isAmenitiesSucceeded = useCallback(() => amenitiesCallStatus === 'success', [amenitiesCallStatus]);

  // Amenities is "complete" only when it has actually finished (success or error)
  // We should NOT treat 'idle' as complete - we need to wait for the Amenities component to make its call
  // However, if amenities stays 'idle' for too long, we use a timeout to proceed
  const isAmenitiesComplete = useCallback(
    () => amenitiesCallStatus === 'success' || amenitiesCallStatus === 'error',
    [amenitiesCallStatus]
  );

  // Track if we've waited long enough for amenities to start
  const amenitiesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [amenitiesTimedOut, setAmenitiesTimedOut] = useState(false);

  // Determine if initial calls are complete (not loading)
  // A call is "complete" only if we have a result (data or error) - not just "not loading"
  // This prevents false positives when SWR hasn't started fetching yet
  const propertyInfoCallComplete = !isPropertyInfoLoading && (!!propertyInfoData || !!propertyInfoError);
  const photogalleryCallComplete = !isPhotogalleryLoading && (!!photogalleryData || !!photogalleryError);
  const initialCallsComplete = propertyInfoCallComplete && photogalleryCallComplete;

  // Determine if both initial calls failed
  const bothInitialCallsFailed = !!propertyInfoError && !!photogalleryError;

  // Determine if at least one initial call succeeded
  const atLeastOneSucceeded = !!propertyInfoData || !!photogalleryData;

  // Effect to trigger retry when both initial calls fail (LEGACY MODE ONLY)
  // In SWR mode, SWR handles retries automatically with errorRetryCount: 3
  // This effect should only trigger ONCE per modal open
  useEffect(() => {
    // Guard: In SWR mode, skip manual retry - SWR handles retries automatically
    if (enableUseSWR) {
      hqvLog.log('Retry effect - SKIPPED: SWR mode handles retries automatically');
      return;
    }

    // Log every time this effect runs to trace re-entry
    hqvLog.log('Retry effect triggered - current state:', {
      initialCallsComplete,
      bothInitialCallsFailed,
      retryAttempted,
      retryInProgressRef: retryInProgressRef.current,
      failureProcessedRef: failureProcessedRef.current,
      hasRefetchPropertyInfo: !!refetchPropertyInfo,
      hasRefetchPhotogallery: !!refetchPhotogallery,
      modalOpenState,
      propertyInfoError: !!propertyInfoError,
      photogalleryError: !!photogalleryError,
      propertyInfoData: !!propertyInfoData,
      photogalleryData: !!photogalleryData,
    });

    // Guard: Don't retry if failure already processed (ref check is synchronous - most reliable)
    // This is the PRIMARY guard to prevent multiple retries
    if (failureProcessedRef.current) {
      hqvLog.log('Retry effect - SKIPPED: failureProcessedRef is true (already processed)');
      return;
    }

    // Guard: Don't retry if already attempted (state check - backup guard)
    if (retryAttempted) {
      hqvLog.log('Retry effect - SKIPPED: retryAttempted is true');
      return;
    }

    // Guard: Don't retry if in progress
    if (retryInProgressRef.current) {
      hqvLog.log('Retry effect - SKIPPED: retryInProgressRef is true');
      return;
    }

    // Guard: Wait for initial calls to complete
    if (!initialCallsComplete) {
      hqvLog.log('Retry effect - SKIPPED: initialCallsComplete is false');
      return;
    }

    // Guard: Only retry if both calls failed
    if (!bothInitialCallsFailed) {
      hqvLog.log('Retry effect - SKIPPED: bothInitialCallsFailed is false');
      return;
    }

    // Guard: Need refetch functions
    if (!refetchPropertyInfo || !refetchPhotogallery) {
      hqvLog.log('Retry effect - SKIPPED: missing refetch functions');
      return;
    }

    // Guard: Modal must be open
    if (!modalOpenState) {
      hqvLog.log('Retry effect - SKIPPED: modalOpenState is false');
      return;
    }

    hqvLog.log('🔄 RETRY STARTING - Both PropertyInfo and PhotoGallery calls failed (retry count: 1)');

    // Set all guards IMMEDIATELY and SYNCHRONOUSLY before any async operation
    // This prevents any re-entry even if React batches state updates
    failureProcessedRef.current = true;
    retryInProgressRef.current = true;
    setRetryAttempted(true);
    setIsRetrying(true);

    // Trigger retries using SWR's refetch
    const propertyInfoRetryPromise = refetchPropertyInfo()
      .then(data => {
        hqvLog.log('✅ Retry PropertyInfo completed with data:', { hasData: !!data, data });
        setRetryPropertyInfoSucceeded(!!data);
        return { success: !!data, data };
      })
      .catch(err => {
        hqvLog.log('❌ Retry PropertyInfo failed with error:', err);
        setRetryPropertyInfoSucceeded(false);
        return { success: false, error: err };
      });

    const photogalleryRetryPromise = refetchPhotogallery()
      .then(data => {
        hqvLog.log('✅ Retry Photogallery completed with data:', { hasData: !!data, data });
        setRetryPhotogallerySucceeded(!!data);
        return { success: !!data, data };
      })
      .catch(err => {
        hqvLog.log('❌ Retry Photogallery failed with error:', err);
        setRetryPhotogallerySucceeded(false);
        return { success: false, error: err };
      });

    Promise.all([propertyInfoRetryPromise, photogalleryRetryPromise])
      .then(results => {
        hqvLog.log('🔄 RETRY Promise.all resolved:', results);
      })
      .finally(() => {
        hqvLog.log('🔄 RETRY COMPLETED - setting isRetrying to false (retry will NOT run again)');
        setIsRetrying(false);
        // NOTE: We intentionally do NOT reset retryInProgressRef here
        // failureProcessedRef stays true to prevent any future retries in this modal session
      });
  }, [
    enableUseSWR,
    initialCallsComplete,
    bothInitialCallsFailed,
    retryAttempted,
    refetchPropertyInfo,
    refetchPhotogallery,
    modalOpenState,
    propertyInfoError,
    photogalleryError,
    propertyInfoData,
    photogalleryData,
  ]);

  // Effect to determine fallback mode after retry completes (or after SWR exhausts retries)
  useEffect(() => {
    hqvLog.log('Fallback mode determination effect - current state:', {
      enableUseSWR,
      retryAttempted,
      isRetrying,
      retryPropertyInfoSucceeded,
      retryPhotogallerySucceeded,
      amenitiesCallStatus,
      isAmenitiesComplete: isAmenitiesComplete(),
      isAmenitiesSucceeded: isAmenitiesSucceeded(),
      initialCallsComplete,
      bothInitialCallsFailed,
    });

    // SWR MODE: Determine fallback mode based on final SWR state
    // SWR has already retried automatically, so if we have errors, retries are exhausted
    if (enableUseSWR) {
      // Wait for calls to complete
      if (!initialCallsComplete) {
        hqvLog.log('Fallback mode effect (SWR) - SKIPPED: calls not complete');
        return;
      }

      // If at least one succeeded, normal mode (handled by another effect)
      if (atLeastOneSucceeded) {
        hqvLog.log('Fallback mode effect (SWR) - SKIPPED: at least one call succeeded');
        return;
      }

      // Both calls failed after SWR's automatic retries - check amenities
      if (!bothInitialCallsFailed) {
        hqvLog.log('Fallback mode effect (SWR) - SKIPPED: not both calls failed');
        return;
      }

      // Prevent duplicate processing
      if (failureProcessedRef.current) {
        hqvLog.log('Fallback mode effect (SWR) - SKIPPED: already processed');
        return;
      }

      hqvLog.log('Fallback mode effect (SWR) - Both calls failed after SWR retries, checking amenities');
      failureProcessedRef.current = true;

      // Wait for amenities to complete
      const amenitiesReady = isAmenitiesComplete() || amenitiesTimedOut;

      if (!amenitiesReady) {
        hqvLog.log('Fallback mode effect (SWR) - WAITING: amenities call not complete yet');
        if (amenitiesCallStatus === 'idle' && !amenitiesTimeoutRef.current) {
          hqvLog.log('Starting amenities timeout (3 seconds)');
          amenitiesTimeoutRef.current = setTimeout(() => {
            hqvLog.log('Amenities timeout reached - proceeding with fallback decision');
            setAmenitiesTimedOut(true);
          }, 3000);
        }
        // Reset failureProcessedRef so we can re-enter when amenities completes
        failureProcessedRef.current = false;
        return;
      }

      // Clear timeout
      if (amenitiesTimeoutRef.current) {
        clearTimeout(amenitiesTimeoutRef.current);
        amenitiesTimeoutRef.current = null;
      }

      if (isAmenitiesSucceeded()) {
        hqvLog.log('🔶 (SWR) All retries failed but amenities succeeded - setting fallback mode to amenities_only');
        setHqvFallbackMode('amenities_only');
        if (!datalayerCalledForFallback.current) {
          datalayerCalledForFallback.current = true;
          setDatalayerCallStatus('called');
          hqvLog.log('Calling data layer with search_fallback_exists=true (amenities_only mode)');
          callDataLayer(
            resolvedUrl,
            sessionData,
            headersData,
            query,
            propertyId,
            brandCode,
            datalayerParams,
            datalayerProperties,
            currentLocale,
            true,
            'HQV_Failure'
          );
        }
      } else {
        hqvLog.log('🔴 (SWR) All calls failed - setting fallback mode to CLOSED');
        setHqvFallbackMode('closed');
        if (!datalayerCalledForFallback.current) {
          datalayerCalledForFallback.current = true;
          setDatalayerCallStatus('called');
          hqvLog.log('Calling data layer with search_fallback_exists=false (closed mode)');
          callDataLayer(
            resolvedUrl,
            sessionData,
            headersData,
            query,
            propertyId,
            brandCode,
            datalayerParams,
            datalayerProperties,
            currentLocale,
            false,
            'HQV_Failure'
          );
        }
      }
      return;
    }

    // LEGACY MODE: Original logic - wait for manual retry to complete
    if (!retryAttempted || isRetrying) {
      hqvLog.log('Fallback mode effect - SKIPPED: retry not attempted or still retrying');
      return;
    }

    // Check if retry results are available
    if (retryPropertyInfoSucceeded === null || retryPhotogallerySucceeded === null) {
      hqvLog.log('Fallback mode effect - SKIPPED: retry results not yet available');
      return;
    }

    const retrySucceeded = retryPropertyInfoSucceeded || retryPhotogallerySucceeded;

    if (retrySucceeded) {
      hqvLog.log('Retry succeeded - setting fallback mode to normal');
      setHqvFallbackMode('normal');

      // Call data layer with fallback values when retry succeeds
      if (!datalayerCalledForFallback.current) {
        datalayerCalledForFallback.current = true;
        setDatalayerCallStatus('called');
        hqvLog.log('Calling data layer with search_fallback_exists=true (retry succeeded)');
        callDataLayer(
          resolvedUrl,
          sessionData,
          headersData,
          query,
          propertyId,
          brandCode,
          datalayerParams,
          datalayerProperties,
          currentLocale,
          true,
          'HQV_Failure'
        );
      }
      return;
    }

    // Both retries failed - check amenities status
    // Wait for amenities to complete (success or error), or timeout if it stays idle
    const amenitiesReady = isAmenitiesComplete() || amenitiesTimedOut;

    if (!amenitiesReady) {
      hqvLog.log('Fallback mode effect - WAITING: amenities call not complete yet', {
        amenitiesCallStatus,
        amenitiesTimedOut,
        isAmenitiesComplete: isAmenitiesComplete(),
      });

      // Start a timeout if amenities is still idle - give it 3 seconds to start
      if (amenitiesCallStatus === 'idle' && !amenitiesTimeoutRef.current) {
        hqvLog.log('Starting amenities timeout (3 seconds)');
        amenitiesTimeoutRef.current = setTimeout(() => {
          hqvLog.log('Amenities timeout reached - proceeding with fallback decision');
          setAmenitiesTimedOut(true);
        }, 3000);
      }
      return;
    }

    // Clear timeout if we got here
    if (amenitiesTimeoutRef.current) {
      clearTimeout(amenitiesTimeoutRef.current);
      amenitiesTimeoutRef.current = null;
    }

    hqvLog.log('Amenities status check - determining final fallback mode:', {
      amenitiesCallStatus,
      amenitiesTimedOut,
      isAmenitiesSucceeded: isAmenitiesSucceeded(),
      isAmenitiesComplete: isAmenitiesComplete(),
    });

    if (isAmenitiesSucceeded()) {
      hqvLog.log('🔶 Retry failed but amenities succeeded - setting fallback mode to amenities_only');
      setHqvFallbackMode('amenities_only');

      // Call data layer with fallback values when amenities_only mode
      if (!datalayerCalledForFallback.current) {
        datalayerCalledForFallback.current = true;
        setDatalayerCallStatus('called');
        hqvLog.log('Calling data layer with search_fallback_exists=true (amenities_only mode)');
        callDataLayer(
          resolvedUrl,
          sessionData,
          headersData,
          query,
          propertyId,
          brandCode,
          datalayerParams,
          datalayerProperties,
          currentLocale,
          true,
          'HQV_Failure'
        );
      }
    } else {
      hqvLog.log('🔴 All calls failed - setting fallback mode to CLOSED');
      setHqvFallbackMode('closed');

      // Call data layer with fallback values when closed mode
      if (!datalayerCalledForFallback.current) {
        datalayerCalledForFallback.current = true;
        setDatalayerCallStatus('called');
        hqvLog.log('Calling data layer with search_fallback_exists=false (closed mode)');
        callDataLayer(
          resolvedUrl,
          sessionData,
          headersData,
          query,
          propertyId,
          brandCode,
          datalayerParams,
          datalayerProperties,
          currentLocale,
          false,
          'HQV_Failure'
        );
      }
    }
  }, [
    enableUseSWR,
    initialCallsComplete,
    bothInitialCallsFailed,
    atLeastOneSucceeded,
    retryAttempted,
    isRetrying,
    retryPropertyInfoSucceeded,
    retryPhotogallerySucceeded,
    amenitiesCallStatus,
    amenitiesTimedOut,
    isAmenitiesComplete,
    isAmenitiesSucceeded,
    setHqvFallbackMode,
    setDatalayerCallStatus,
    resolvedUrl,
    sessionData,
    headersData,
    query,
    propertyId,
    brandCode,
    datalayerParams,
    datalayerProperties,
    currentLocale,
  ]);

  // Effect to set normal mode when initial calls succeed (no retry needed)
  useEffect(() => {
    if (initialCallsComplete && atLeastOneSucceeded && !retryAttempted) {
      // At least one call succeeded on first try - normal mode
      if (hqvFallbackMode !== 'normal') {
        setHqvFallbackMode('normal');
      }
    }
  }, [initialCallsComplete, atLeastOneSucceeded, retryAttempted, hqvFallbackMode, setHqvFallbackMode]);

  // Effect to call datalayer when initial calls succeed (no retry needed)
  // This centralizes all datalayer calls in this hook
  // IMPORTANT: Only call datalayer when modal is open AND we have fresh data for this modal session
  useEffect(() => {
    hqvLog.log('Datalayer success effect - checking conditions:', {
      modalOpenState,
      initialCallsComplete,
      propertyInfoCallComplete,
      photogalleryCallComplete,
      atLeastOneSucceeded,
      retryAttempted,
      isRetrying,
      propertyId,
      datalayerCalledForSuccess: datalayerCalledForSuccess.current,
      datalayerCalledForFallback: datalayerCalledForFallback.current,
      hasPropertyInfoData: !!propertyInfoData,
      hasPhotogalleryData: !!photogalleryData,
      hasPropertyInfoError: !!propertyInfoError,
      hasPhotogalleryError: !!photogalleryError,
    });

    // Guard: Only proceed if modal is open
    if (!modalOpenState) {
      return;
    }

    // Guard: Don't call if we've already called for success or fallback
    if (datalayerCalledForSuccess.current || datalayerCalledForFallback.current) {
      return;
    }

    // Guard: Don't call if retry is in progress or attempted (fallback logic will handle it)
    if (retryAttempted || isRetrying) {
      return;
    }

    // Guard: Wait for calls to complete
    if (!initialCallsComplete) {
      return;
    }

    // Guard: Only call if at least one call succeeded with data
    if (!atLeastOneSucceeded) {
      return;
    }

    // Guard: Need propertyId
    if (!propertyId) {
      return;
    }

    datalayerCalledForSuccess.current = true;
    setDatalayerCallStatus('called');
    hqvLog.log('Calling data layer for successful initial fetch');
    callDataLayer(
      resolvedUrl,
      sessionData,
      headersData,
      query,
      propertyId,
      brandCode,
      datalayerParams,
      datalayerProperties,
      currentLocale,
      undefined,
      undefined
    );
  }, [
    modalOpenState,
    initialCallsComplete,
    propertyInfoCallComplete,
    photogalleryCallComplete,
    atLeastOneSucceeded,
    retryAttempted,
    isRetrying,
    propertyId,
    propertyInfoData,
    photogalleryData,
    propertyInfoError,
    photogalleryError,
    setDatalayerCallStatus,
    resolvedUrl,
    sessionData,
    headersData,
    query,
    brandCode,
    datalayerParams,
    datalayerProperties,
    currentLocale,
  ]);

  // Effect to close modal when fallback mode is 'closed'
  useEffect(() => {
    hqvLog.log('Fallback mode effect triggered:', { hqvFallbackMode, hasOnCloseModal: !!onCloseModal });
    if (hqvFallbackMode === 'closed' && onCloseModal) {
      hqvLog.log('All HQV calls failed - closing modal');
      onCloseModal();
    }
  }, [hqvFallbackMode, onCloseModal]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (amenitiesTimeoutRef.current) {
        clearTimeout(amenitiesTimeoutRef.current);
      }
    };
  }, []);

  return {
    hqvFallbackMode,
    isRetrying,
    retryAttempted,
  };
};

export default useHQVRetryOrchestration;
